import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../Auth/AuthContext";
import { Alert, Dialog, DialogTitle, DialogContent, Typography, TextField, InputLabel, Button } from "@mui/material";
import { closePdfPopup, openPdfBlob, openPdfPopup } from "../../../utils/api";
import { authenticatedSessionRequestConfig } from "../../../utils/csrf";
import {
  appendCreatedTicket,
  buildTicketCreationContext,
  createTicketSnapshot,
  getTicketCreationErrorMessage,
  runTicketCreationOnce,
} from "../../../utils/ticketCreation";
import { useTickets } from "../../../hooks/useTickets";

const API_URL = process.env.REACT_APP_API_URL;

const TicketEntradaForm = ({openTicketEntradaForm, setOpenTicketEntradaForm, data, areUserAdmin, handleUpdateData}) => {
   const ticketsIngresos = useTickets('ingreso', data.ticketsIngreso);
   const { user } = useAuth();

   const [selectedObservaciones, setSelectedObservaciones] = useState('Ninguna');

  const [selectedCliente, setSelectedCliente] = useState(data.cliente || null);

  const [clientesData, setClientesData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingClientes, setLoadingClientes] = useState(false);

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const submitRef = React.useRef(false);

  const [openDialogClientes, setOpenDialogClientes] = useState(false);

  const rowsPerPage = 5;
  const cacheRef = React.useRef({});

  useEffect(() => {
    if (data.cliente) {
      setSelectedCliente(data.cliente);
      }
  }, [data]);

  useEffect(() => {
    if (!openDialogClientes) return;

    let isMounted = true;

    const fetchClientes = async () => {
      const key = `${searchText}|${currentPage}`;
      const cached = cacheRef.current[key];

      if (cached) {
        setClientesData(cached.results);
        setTotalPages(Math.ceil(cached.count / rowsPerPage));
        return;
      }

      setLoadingClientes(true);

      try {
        const res = await axios.get(`${API_URL}/clientes/`, {
          params: {
            search: searchText,
            page: currentPage,
            page_size: rowsPerPage,
          },
        });

        if (!isMounted) return;

        const results = res.data.results || [];
        const count = res.data.count || 0;

        cacheRef.current[key] = { results, count };

        setClientesData(results);
        setTotalPages(Math.ceil(count / rowsPerPage));

      } catch (err) {
        console.error("Error cargando clientes:", err);
      } finally {
        if (isMounted) setLoadingClientes(false);
      }
    };

    fetchClientes();

    return () => {
      isMounted = false;
    };
  }, [searchText, currentPage, openDialogClientes]);

   const handleCrearTicket = () => runTicketCreationOnce(submitRef, async () => {
    setLoadingSubmit(true);
    setSubmitError('');
    let pdfWindow = null;
    try {
      pdfWindow = openPdfPopup();
      if (!pdfWindow) {
        setSubmitError('Habilita las ventanas emergentes para imprimir el ticket.');
        return;
      }

      const firstTicket = data.ticketsIngreso.length === 0;
      const originalClienteId = data.cliente?.id || null;
      const originalObservaciones = data.observaciones || 'Ninguna';
      const fecha = data.entrada.fecha_entrada;
      const hora = data.entrada.hora_entrada;
      const cambios =
        selectedCliente?.id !== originalClienteId ||
        selectedObservaciones !== originalObservaciones;
      const payload = {
        ...buildTicketCreationContext(
          data.id,
          cambios ? selectedCliente?.id : originalClienteId
        ),
        fecha_ingreso: fecha,
        hora_ingreso: hora,
        estado: firstTicket ? 'Vigente' : 'Modificado',
        observaciones: cambios ? selectedObservaciones : originalObservaciones,
      };
      const requestConfig = await authenticatedSessionRequestConfig(API_URL, axios.get);
      const ticketVigente = ticketsIngresos.find((ticket) => ticket.estado === 'Vigente');
      if (ticketVigente &&
          (ticketVigente.fecha_ingreso !== fecha || ticketVigente.hora_ingreso !== hora)) {
        await axios.put(
          `${API_URL}/tickets-ingreso/${ticketVigente.id}/`,
          { estado: 'Modificado' },
          requestConfig
        );
      }

      const createdTicket = await createTicketSnapshot({
        apiUrl: API_URL,
        tipo: 'ingreso',
        payload,
        requestConfig,
        httpClient: axios,
      });
      const pdfResponse = await axios.get(
        `${API_URL}/tickets-ingreso/${createdTicket.id}/`,
        { responseType: 'blob', withCredentials: true }
      );
      handleUpdateData({
        ...data,
        ticketsIngreso: appendCreatedTicket(data.ticketsIngreso, createdTicket),
      });
      openPdfBlob(pdfResponse.data, pdfWindow);
      setOpenTicketEntradaForm(false);
    } catch (error) {
      setSubmitError(getTicketCreationErrorMessage(error));
      console.error('No fue posible crear el ticket de entrada:', error?.response?.status || error?.message);
      closePdfPopup(pdfWindow);
    } finally {
      setLoadingSubmit(false);
    }
  });

  return (
    <Dialog
      open={openTicketEntradaForm}
      onClose={() => setOpenTicketEntradaForm(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Ticket de Entrada</DialogTitle>
      <DialogContent>
        <Typography variant="h6" gutterBottom sx={{ paddingTop: 2 }}>
          <strong>Empleado:</strong> {user?.nombre}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Fecha de Entrada:</strong> {data.entrada.fecha_entrada}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Hora de Entrada:</strong> {data.entrada.hora_entrada}
        </Typography>

        <InputLabel sx={{ paddingTop: 2 }}>Cliente</InputLabel>

        <TextField
          fullWidth
          value={
            selectedCliente
              ? `${selectedCliente.nombre} ${selectedCliente.apellidos}`
              : ''
          }
          disabled
        />

        {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}

        <Button
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={() => setOpenDialogClientes(true)}
        >
          Buscar Cliente
        </Button>

        <InputLabel sx={{ paddingTop: 2 }}>Observaciones</InputLabel>
        <TextField
          autoFocus
          fullWidth
          value={selectedObservaciones}
          onChange={(e) => setSelectedObservaciones(e.target.value)}
          inputProps={{ maxLength: 200 }}
          sx={{ textTransform: 'uppercase' }}
        />

        <Button
          variant="contained"
          color="primary"
          disabled={loadingSubmit}
          onClick={() => handleCrearTicket()}
          sx={{ marginTop: 2 }}
        >
          {  loadingSubmit ? "Procesando..." : "Imprimir Ticket de Entrada"}
        </Button>
      </DialogContent>

      <Dialog
        open={openDialogClientes}
        onClose={() => setOpenDialogClientes(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Buscar Cliente</DialogTitle>

        <DialogContent>

          <TextField
            fullWidth
            placeholder="Buscar por nombre, documento..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
            sx={{ mb: 2 }}
          />

          {loadingClientes ? (
            <Typography>Cargando...</Typography>
          ) : (
            <>
              {clientesData.map((cliente) => (
                <div
                  key={cliente.id}
                  style={{
                    padding: "10px",
                    borderBottom: "1px solid #ccc",
                    cursor: "pointer"
                  }}
                  onClick={() => {
                    setSelectedCliente(cliente);
                    setOpenDialogClientes(false);
                  }}
                >
                  <strong>
                    {cliente.nombre} {cliente.apellidos}
                  </strong>
                  <br />
                  {cliente.tipo_documento}: {cliente.documento}
                </div>
              ))}
            </>
          )}

          {/* PAGINACIÓN */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Anterior
              </Button>

              <Typography sx={{ mx: 2 }}>
                {currentPage} / {totalPages}
              </Typography>

              <Button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Siguiente
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default TicketEntradaForm;
